import React from "react";
import {
  Cctv, ScanLine, Siren, DoorOpen, Fence, Cable,
  Briefcase, Users, UserCheck,
} from "lucide-react";
import SentinelFAQ from "../components/SentinelFAQ";
import SecurityIllustration from "../components/SecurityIllustration";
import { useReveal } from "../hooks/useReveal";

// wrap = container that reveals on scroll
const RW = ({ className = "", children }) => {
  const [ref, v] = useReveal();
  return (
    <div ref={ref} className={`wrap reveal ${v ? "is-visible" : ""} ${className}`}>
      {children}
    </div>
  );
};

const audience = [
  { icon: Briefcase, label: "For Owners", sub: "Sell without losing what you built", target: "owners" },
  { icon: Users, label: "For Teams", sub: "More support, same culture", target: "teams" },
  { icon: UserCheck, label: "For Customers", sub: "Same local team, deeper resources", target: "customers" },
];

const services = [
  { icon: Cctv, h: "Video Surveillance", p: "Cameras, analytics, and monitoring technology that keep watch inside and around every facility." },
  { icon: ScanLine, h: "Access Control", p: "Cloud-based and on-premise systems that manage who — and what — gets through the door." },
  { icon: Siren, h: "Intrusion & Alarm", p: "Intrusion detection, fire and life-safety alarms, and environmental sensors that flag threats fast." },
  { icon: DoorOpen, h: "Commercial Doors & Entry", p: "Automatic and manual entrances and related hardware engineered for safe, reliable daily use." },
  { icon: Fence, h: "Perimeter & Gates", p: "Automated gates, commercial fencing, and parking control that secure the property line." },
  { icon: Cable, h: "Low-Voltage Cabling", p: "Structured cabling and fiber infrastructure that connect and power every security, network, and communications system." },
];

const values = [
  { tag: "STEWARDSHIP", h: "Your legacy is the asset", p: "We buy companies because of what they are, not despite it. Protecting the reputation you built is the whole investment thesis — in good years and hard ones." },
  { tag: "STRAIGHT TALK", h: "We say what we'll do, then do it", p: "Real numbers, plain language, no re-trading after the LOI. If we're not the right buyer for you, we'll tell you that too — and often we can point you to who is." },
  { tag: "PEOPLE FIRST", h: "Technicians are the business", p: "Trucks and inventory don't earn trust — people do. We invest in training, career paths, and retention for the crews who carry your name into the field." },
  { tag: "LOCAL FOREVER", h: "Local brand, local decisions", p: "Your customers keep the name and the faces they know. We work behind the scenes — purchasing, back office, recruiting — so your team can say yes more often." },
  { tag: "LONG HORIZON", h: "Buying to build, not to flip", p: "We're assembling the best security platform in western North America and Canada, company by company. That only works if every company we buy is stronger five years after close." },
  { tag: "CRAFT", h: "Excellent work, measured honestly", p: "We hold ourselves to the standard your best customer would set: clean installs, fast service, honest scoping — and we track it every single week." },
];

const steps = [
  { t: "Confidential conversation", h: "We talk, founder to founder", p: "No brokers needed, no pitch decks required. Tell us about your company, your people, and what you want your exit to look like. Everything is confidential from the first call." },
  { t: "Understanding the business", h: "We review, you get a real number", p: "With an NDA in place, we review your financials and your service book. You receive an indicative offer — and we walk you through exactly how we arrived at it." },
  { t: "Letter of intent", h: "Terms agreed, in writing", p: "Price, structure, your future role, and commitments to your team — fixed in an LOI before diligence begins. We don't sign letters we intend to renegotiate." },
  { t: "Diligence and close", h: "Confirm, close, and fund", p: "We complete confirmatory diligence and plan the employee and customer announcement with you — nobody hears the news secondhand." },
  { t: "The long term", h: "Your company, stronger", p: "The playbook begins: new recurring-revenue services for your customers, better purchasing, back-office relief, and investment in your people. Your name on the door, our support behind it." },
];

const keeps = [
  "Your company name, brand, and trucks",
  "Your team, your managers, your service culture",
  "Your customer relationships and service model",
  "Local decision-making on jobs, hiring, and vendors",
  "Your role — advisory, leadership, or a planned handover. Your call.",
];

const Northstar = () => {
  return (
    <main>
      {/* HERO */}
      <header className="hero dark">
        <div className="wrap">
          <div className="hero-grid">
            <div>
              <span className="eyebrow">For founders of security integration companies</span>
              <h1>A permanent home for the business <em>you built.</em></h1>
              <p className="lede">
                Northstar acquires exceptional security integration and alarm companies across
                western North America and Canada — and keeps what made them exceptional. Your brand
                stays. Your people stay. Your customers keep the team they trust.
              </p>
              <div className="hero-ctas">
                <a className="btn btn-amber" href="#contact">Start a confidential conversation</a>
                <a className="btn btn-ghost" href="#process">See how selling works</a>
              </div>
              <p className="hero-note">// NO OBLIGATION · NO BROKERS REQUIRED · FOUNDER TO FOUNDER</p>
            </div>
            <SecurityIllustration className="hero-illo" />
          </div>

          <div className="aud-grid">
            {audience.map((a) => {
              const Icon = a.icon;
              return (
                <a className="aud-card" href="#faq" key={a.label}>
                  <span className="aud-ic"><Icon size={22} /></span>
                  <span className="aud-tx">
                    <b>{a.label}</b>
                    <span>{a.sub}</span>
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </header>

      {/* PROBLEM */}
      <section className="problem" id="why">
        <RW className="problem-grid">
          <div>
            <span className="eyebrow">The problem with most buyers</span>
            <p className="big">Most owners are right to be wary of selling. Too many acquirers strip the brand, churn the technicians, and treat a life's work as <em>a line on a spreadsheet.</em></p>
          </div>
          <div>
            <p>You spent decades earning your reputation — one service call, one install, one handshake at a time. Then a buyer shows up, promises continuity, and eighteen months later the name is gone, the senior techs have quit, and your longest-standing customers are calling you at home asking what happened.</p>
            <p>We started Northstar because this industry deserves a better kind of buyer: one that comes from the trade, understands what a service reputation is actually worth, and builds companies up instead of hollowing them out.</p>
            <p><strong>We're not a fund passing through. We're operators from this industry, building for the long term.</strong></p>
          </div>
        </RW>
      </section>

      {/* SERVICES */}
      <section className="services" id="services">
        <RW>
          <span className="eyebrow">What our companies do</span>
          <h2 className="display">What our companies install &amp; maintain.</h2>
          <div className="svc-grid">
            {services.map((s) => {
              const Icon = s.icon;
              return (
                <div className="svc" key={s.h}>
                  <span className="ic"><Icon size={26} /></span>
                  <h3>{s.h}</h3>
                  <p>{s.p}</p>
                </div>
              );
            })}
          </div>
        </RW>
      </section>

      {/* APPROACH */}
      <section className="approach dark" id="approach">
        <RW className="approach-grid">
          <div>
            <span className="eyebrow">Our approach</span>
            <h2 className="display">Built by the trade, for the trade.</h2>
            <p className="lede">Northstar was founded by an operator who has worked every seat in the security channel — installation, integration, manufacturer representation, and remote monitoring. We don't need your industry explained to us. We've lived it.</p>
          </div>
          <div>
            <p style={{ color: "#C7D4E6" }}>That background changes how we buy and how we operate. We can read a service book, a monitoring contract, and an install backlog without a consultant translating. We know which vendor relationships matter and why your best technician is worth protecting at almost any cost.</p>
            <div className="stat-row">
              <div className="stat"><b>20+ yrs</b><span>operating in the security channel</span></div>
              <div className="stat"><b>100%</b><span>of brands and local teams retained</span></div>
            </div>
          </div>
        </RW>
      </section>

      {/* VALUES */}
      <section className="values" id="values">
        <RW>
          <span className="eyebrow">Our commitments</span>
          <h2 className="display">How we show up — for owners, teams, and customers.</h2>
          <div className="vgrid">
            {values.map((v) => (
              <div className="vcard" key={v.tag}>
                <span className="vnum">{v.tag}</span>
                <h3>{v.h}</h3>
                <p>{v.p}</p>
              </div>
            ))}
          </div>
        </RW>
      </section>

      {/* LEDGER */}
      <section className="ledger" id="ledger">
        <RW>
          <span className="eyebrow">The deal, plainly</span>
          <h2 className="display">What stays exactly as it is.</h2>
          <div className="ledger-grid">
            <div className="ledger-col keeps">
              <h3>What stays exactly as it is</h3>
              <ul>
                {keeps.map((k) => (
                  <li key={k}><span className="mark">[=]</span>{k}</li>
                ))}
              </ul>
            </div>
          </div>
        </RW>
      </section>

      {/* PROCESS */}
      <section className="process" id="process">
        <RW>
          <span className="eyebrow">Selling to Northstar</span>
          <h2 className="display">A straightforward process, start to finish.</h2>
          <div className="steps">
            {steps.map((s) => (
              <div className="step" key={s.h}>
                <span className="stime">{s.t}</span>
                <h3>{s.h}</h3>
                <p>{s.p}</p>
              </div>
            ))}
          </div>
        </RW>
      </section>

      {/* FAQ */}
      <SentinelFAQ />

      {/* CTA */}
      <section className="cta" id="contact">
        <div className="wrap cta-inner">
          <div>
            <h2>Thinking about the next chapter?</h2>
            <p>One confidential conversation. No obligation, no pressure, no brokers required. Worst case, you leave with a clear sense of what your company is worth — and to whom.</p>
          </div>
          <div className="cta-side">
            <a className="btn" href="mailto:info@northstarservicepartners.com?subject=Confidential%20conversation">Start the conversation</a>
            <span className="sub">INFO@NORTHSTARSERVICEPARTNERS.COM</span>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Northstar;
